import { normalizePortalPath } from '../../../../utilities/normalizePortalPath';
import React from 'react';
import { createFileRoute, useNavigate, useRouterState } from '@tanstack/react-router'
import type { FileRouteTypes } from '@tanstack/react-router';
import { useAuth } from '../../../../contexts/AuthContext';
import { Unauthorized } from '../../../../components/Unauthorized';
import { Chart, PatientDoc, Product } from '@usacsmideo/types';
import useChart from '../../../../hooks/useChart';
import usePatientDoc from '../../../../hooks/usePatientDoc';
import useProducts from '../../../../hooks/useProducts';
import { SelectedProduct } from '../../../../components/products/SelectedProduct';
import { Button } from '../../../../components';
import { Heading } from '../../../../components/Heading';
import * as Sentry from '@sentry/browser';

import { classNames } from '../../../../style/classNames';
import { updateNavObjAndGetNextScreen } from '../../../../data/navigation/updateNavObjAndGetNextScreen';
import { useUpdateUserDoc } from '../../../../hooks/useUpdateUserDoc';
export const Route = createFileRoute('/_authenticated/$patientId/payment/confirmPackage')({
  component: ProductConfirmationPage,
});

function ProductConfirmationPage() {
  const [selected, setSelected] = React.useState<string>('');
  const [selectedProduct, setSelectedProduct] = React.useState<Product>();
  const [productConfirmed, setProductConfirmed] = React.useState<boolean>(false);
  const { patientId } = Route.useParams();
  const { currentUser } = useAuth();
  const chartQuery = useChart(patientId);
  const chart = chartQuery.data as Chart;
  const patientDocQuery = usePatientDoc(patientId);
  const patientInfo = patientDocQuery.data as PatientDoc;
  const productsDocQuery = useProducts();
  const productsDocData = productsDocQuery.data as Product[];
  const { mutate: mutatePatientDoc } = useUpdateUserDoc();
  const navigate = useNavigate();

  const routerState = useRouterState();
  const currentLocation = routerState.location;
  const pathname = currentLocation.pathname;

  const handleNextButtonClick = async () => {
    const res = await updateNavObjAndGetNextScreen({
      patientId,
      currentPage: pathname,
      patientInfo: patientInfo,
      chart: chart,
    });

    if ('incompleteMessage' in res) {
      // THE PATIENT HAS NOT COMPLETED THE NECESSARY SCREENS
      alert(`Incomplete Information: ${res.incompleteMessage}`);
      return;
    }
    if ('errorMessage' in res) {
      // FIRESTORE ERROR
      alert(`Error: ${res.errorMessage}`);
      return;
    }
    // NAVIGATION COMPLETE — PERSIST FINAL STATE AND NAVIGATE HOME
    if ('complete' in res) {
      mutatePatientDoc(
        { patientId: patientId, navObj: res.updatedNavObj },
        {
          onSuccess: () => {
            navigate({ to: '/' });
          },
          onError: (error: Error) => {
            Sentry.captureException(
              new Error(
                `Error updating patient navigation object for patient ${patientId}: ${error.message}`,
              ),
            );
            console.error('Error updating patient navigation object:', error);
            alert(
              'There was an error updating your information. Please try again later or contact support if this issue persists.',
            );
          },
        },
      );
      return;
    }


    mutatePatientDoc(
      {
        patientId: patientId,
        navObj: res.updatedNavObj,
      },
      {
        onSuccess: () => {
          // NAVIGATE TO THE NEXT SCREEN
          navigate({ to: normalizePortalPath(res.nextScreen.portalPath) as FileRouteTypes['to'] });
        },
        onError: (error: Error) => {
          Sentry.captureException(
            new Error(
              `Error updating patient navigation object for patient ${patientId}: ${error.message}`,
            ),
          );
          console.error('Error updating patient navigation object:', error);
          alert(
            'There was an error updating your information. Please try again later or contact support if this issue persists.',
          );
        },
      },
    );
  };

  // SET SELECTED PRODUCT
  React.useEffect(() => {
    if (productsDocData && chart) {
      let productFullName = '';
      switch (chart?.PRODUCT_INFO?.selectedProduct) {
        case 'essential':
          setSelected('Self Guided Package');
          productFullName = 'Self Guided Package';
          break;
        case 'vital':
          setSelected('Vital Package');
          productFullName = 'Vital Package';
          break;
        case 'vip':
          setSelected('Physician Guided Package');
          productFullName = 'Physician Guided Package';
          break;
        case 'Self Guided Package':
          setSelected('Self Guided Package');
          productFullName = 'Self Guided Package';
          break;
        case 'Vital Package':
          setSelected('Vital Package');
          productFullName = 'Vital Package';
          break;
        case 'Physician Guided Package':
          setSelected('Physician Guided Package');
          productFullName = 'Physician Guided Package';
          break;
        default:
          break;
      }
      setSelectedProduct(productsDocData.find((product) => product.name === productFullName));
    }
  }, [productsDocData, chart]);

  const handleConfirm = () => {
    setProductConfirmed(true);
  };

  if (chartQuery.isLoading || patientDocQuery.isLoading || !chart) {
    return <div>Loading patient information...</div>;
  }
  if (!currentUser) {
    return <div>Attempting to load current user</div>;
  }
  if (currentUser?.uid !== patientId) {
    return <Unauthorized />;
  }
  if (currentUser?.uid === patientId && productsDocData?.length > 0) {
    return (
      <>
        <div id="medicare-abn-page" className="flex w-screen flex-col items-center justify-center">
          <Heading id="medicare-abn-page-header" textContent="Product Confirmation" />
          <div id="product-cards-container" className="flex w-10/12 flex-row flex-wrap justify-around">
            {selectedProduct && <SelectedProduct product={selectedProduct} selected={selected} />}
          </div>
          <Button type="button" id="confirm-product" onClick={handleConfirm} textContent="Confirm Product Selection" />
          {productConfirmed && (
            <div id="nav" className="flex flex-row">
              <div className="flex flex-col items-center">
                <button
                  id="submit-form-info"
                  title="Submit Form Info"
                  onClick={handleNextButtonClick}
                  disabled={false}
                  className={classNames.nextButton}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </>
    );
  }

  return <div>Loading...</div>;
}
